import React, { useState, useRef } from 'react';
import { Icons } from '../../Icon.jsx';
import { TAILWIND_COLORS } from '../../../utils/TailwindParser.js';
import { parseThemeColor } from '../../../utils/theme-helper.js';
import ColorPicker from 'aldoo-ra/HtmlEditor/StyleClassEditor/components/ColorPicker.jsx';
import ThemeColorBrowser from '../../ThemeColorBrowser.jsx';
import DropdownSelect from 'aldoo-ra/DropdownSelect';
import Input from 'aldoo-ra/Input';

const PropertyControl = ({ def, value, onChange, disabled = false }) => {
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showThemeBrowser, setShowThemeBrowser] = useState(false);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
    const [themePickerPosition, setThemePickerPosition] = useState({ top: 0, left: 0 });
    const colorButtonRef = useRef(null);
    const themeButtonRef = useRef(null);
    const originalValueRef = useRef(value);

    const handleOpenColorPicker = () => {
        if (colorButtonRef.current) {
            originalValueRef.current = value;
            const rect = colorButtonRef.current.getBoundingClientRect();
            const pickerWidth = 384;
            const pickerHeight = 450;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const gap = 8;

            let top = rect.top - pickerHeight - gap;
            let left = rect.left - pickerWidth + rect.width; // Align right edge
            let positionedAbove = true;

            if (top < gap) {
                top = rect.bottom + gap;
                positionedAbove = false;
            }

            if (!positionedAbove && top + pickerHeight > viewportHeight - gap) {
                const spaceAbove = rect.top;
                const spaceBelow = viewportHeight - rect.bottom;
                if (spaceAbove > spaceBelow) {
                    top = Math.max(gap, rect.top - pickerHeight - gap);
                } else {
                    top = Math.min(rect.bottom + gap, viewportHeight - pickerHeight - gap);
                }
            }

            if (left + pickerWidth > viewportWidth - gap) left = viewportWidth - pickerWidth - gap;
            if (left < gap) left = gap;

            setPickerPosition({ top, left });
        }
        setShowColorPicker(true);
    };

    const handleOpenThemeBrowser = () => {
        if (themeButtonRef.current) {
            const rect = themeButtonRef.current.getBoundingClientRect();
            const pickerWidth = 256; // w-64
            const pickerHeight = 400;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const gap = 8;

            let top = rect.bottom + gap;
            let left = rect.left;

            if (top + pickerHeight > viewportHeight - gap) {
                top = rect.top - pickerHeight - gap;
            }

            if (left + pickerWidth > viewportWidth - gap) left = viewportWidth - pickerWidth - gap;
            if (left < gap) left = gap;

            setThemePickerPosition({ top, left });
        }
        setShowThemeBrowser(true);
    };

    if (def.type === 'color') {
        // Try to resolve the actual color for display
        const displayColor = (() => {
            if (!value) return undefined;

            // Check if it's already a hex/rgb/hsl color
            if (value.startsWith('#') || value.startsWith('rgb') || value.startsWith('hsl')) {
                return value;
            }

            // Try to parse as Tailwind color (e.g., red-500)
            const parts = value.split('-');
            if (parts.length >= 2) {
                const colorName = parts.slice(0, -1).join('-');
                const shade = parts[parts.length - 1];
                if (TAILWIND_COLORS[colorName] && TAILWIND_COLORS[colorName][shade]) {
                    return TAILWIND_COLORS[colorName][shade];
                }
            }

            // Try theme color
            const resolved = parseThemeColor(value);
            if (resolved) {
                return `rgb(${resolved.replace(/ /g, ', ')})`;
            }

            return undefined;
        })();

        const colorStyle = displayColor ? { backgroundColor: displayColor } : { backgroundColor: '#ccc' };

        return (
             <div className="flex items-center gap-2 relative">
                 <button
                    ref={themeButtonRef}
                    type="button"
                    onClick={handleOpenThemeBrowser}
                    disabled={disabled}
                    className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 border border-transparent hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    title="Browse Theme Colors"
                 >
                    <Icons.Grid className="w-4 h-4"/>
                 </button>

                 <Input
                    variant="admin"
                    value={value || ''}
                    onChange={e => !disabled && onChange(typeof e === 'string' ? e : e.target.value)}
                    className="w-full"
                    placeholder="e.g. red-500 or #ff0000"
                    disabled={disabled}
                    readOnly={disabled}
                 />
                 <button
                    ref={colorButtonRef}
                    type="button"
                    onClick={handleOpenColorPicker}
                    disabled={disabled}
                    className="w-6 h-6 rounded border border-gray-300 hover:border-gray-400 cursor-pointer flex-shrink-0 transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    style={colorStyle}
                    title="Open color picker"
                 />

                 {showColorPicker && (
                     <>
                         {/* Backdrop */}
                         <div className="fixed inset-0 z-[9998]" onClick={() => setShowColorPicker(false)} />

                         {/* Color Picker */}
                         <div
                             className="fixed z-[9999]"
                             style={{
                                 top: `${pickerPosition.top}px`,
                                 left: `${pickerPosition.left}px`
                             }}
                         >
                             <ColorPicker
                                 initialColor={displayColor || '#000000'}
                                 onChange={(color) => {
                                     // Real-time update as user drags
                                     onChange(color);
                                 }}
                                 onColorSelect={(color) => {
                                     onChange(color);
                                     setShowColorPicker(false);
                                 }}
                                 onCancel={() => {
                                     // Restore original value (e.g. 'primary') instead of the resolved hex
                                     onChange(originalValueRef.current);
                                 }}
                                 onClose={() => setShowColorPicker(false)}
                                 showSwatches={true}
                                 alpha={false}
                             />
                         </div>
                     </>
                 )}

                 {showThemeBrowser && (
                     <>
                         {/* Backdrop */}
                         <div className="fixed inset-0 z-[9998]" onClick={() => setShowThemeBrowser(false)} />

                         {/* Theme Browser */}
                         <div
                             className="fixed z-[9999]"
                             style={{
                                 top: `${themePickerPosition.top}px`,
                                 left: `${themePickerPosition.left}px`
                             }}
                         >
                             <ThemeColorBrowser
                                 value={value}
                                 onChange={(newColor) => {
                                     onChange(newColor);
                                     setShowThemeBrowser(false);
                                 }}
                                 onClose={() => setShowThemeBrowser(false)}
                             />
                         </div>
                     </>
                 )}
             </div>
        );
    }
    if (def.type === 'select') {
        const options = (def.options || []).map(opt => ({
            label: opt,
            value: opt,
            name: opt
        }));

        const currentValue = value ? { label: value, value, name: value } : null;
        // Only allow empty selection if there's no default value
        const canBeEmpty = !def.default;

        return (
            <div className={`w-full ${disabled ? 'pointer-events-none' : ''}`}>
                <DropdownSelect
                    options={options}
                    value={currentValue}
                    onChange={(val) => {
                        if (disabled) return;
                        // DropdownSelect returns array if multi, or single value depending on logic
                        // Based on handleCheckboxChange for single: updatedSelection = [option]
                        const selection = Array.isArray(val) ? val[0] : val;
                        const newValue = selection?.value || '';
                        // If empty and property has default, use default instead of empty
                        onChange(newValue || (def.default || ''));
                    }}
                    searchEnabled={true}
                    creatable={true}
                    editable={true}
                    showCheckbox={false}
                    enableResetButton={!disabled && canBeEmpty}
                    allowEmptySelection={canBeEmpty}
                    placeholder="Default"
                    variant="admin"
                    className={`!py-0 ${disabled ? 'opacity-50' : ''}`}
                />
            </div>
        );
    }
    if (def.type === 'toggle') {
        return (
            <div className="flex gap-2">
                {def.options.map(opt => (
                     <button
                        key={opt.value}
                        onClick={() => !disabled && onChange(value === opt.value ? (opt.off || '') : opt.value)}
                        disabled={disabled}
                        className={`px-2 py-1 text-xs border rounded ${value === opt.value ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                     >
                         {opt.label}
                     </button>
                ))}
            </div>
        );
    }
    return null;
};

export default PropertyControl;
